import { all, put, race, take, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getLocale, getToken, getCustomData } from '@global/selectors'
import config from '@config'

import { BLOCK_DATA_LOADED } from '../../../../../../../../../components/Workspace/components/HocBlock/constants'
import { blockSaveCustomData } from '../../../../../../../../../components/Workspace/components/HocBlock/actions'

import {
    PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE,
    PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST,
    PERSONS_BENEFICIAL_OWNERS_TABLE_LOADING_TRUE,
    PERSONS_BENEFICIAL_OWNERS_TABLE_LOADING_FALSE,
    PERSONS_BENEFICIAL_OWNERS_TABLE_DATA_RECEIVED,
} from './constants'

import {
    personsBeneficialOwnersTableDataReceived,
    personsBeneficialOwnersTableChangeRequest,
    personsBeneficialOwnersTableLoadingTrue,
    personsBeneficialOwnersTableLoadingFalse,
} from './actions'

const PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST_SUCCESS = (id) =>
    `${PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST(id)}_${SUCCESS}`
const PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST_FAILED = (id) =>
    `${PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST(id)}_${FAILED}`

function* onDataLoaded(_action) {
    const blockKey = _action?.data?.blockKey
    if (blockKey !== config.modules.report.blocks.persons.key) return null

    const module = _action?.data?.module
    const id = _action?.data?.id
    if (!module || !id || !blockKey) return null

    yield put(
        personsBeneficialOwnersTableDataReceived(module, id, blockKey, {
            isLoading: false,
            data: _action?.data?.data?.[4]?.beneficialOwners ?? [],
            pagination: _action?.data?.data?.[4]?.query?.pagination ?? {},
            sorted: _action?.data?.data?.[4]?.query?.sort?.sortedColumns ?? [],
            filter: _action?.data?.data?.[4]?.query?.filter ?? {},
        })
    )

    return null
}

function* personsBeneficialOwnersTableSaga(action) {
    const module = action?.data?.module
    const blockKey = action?.data?.blockKey
    const id = action?.data?.id
    const level = action?.data?.level
    const queryParams = action?.data?.queryParams

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level) return null

    yield put(personsBeneficialOwnersTableLoadingTrue(module, id, blockKey, { isLoading: true }))

    const filters = _.reduce(
        queryParams?.filtered,
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': (queryParams?.sorted ?? []).map((val) => `${val?.id}${val?.desc ? ':desc' : ':asc'}`),
        page: queryParams?.pagination?.page,
        rows: queryParams?.pagination?.rowsPerPage,
        ...filters,
    }

    yield put(personsBeneficialOwnersTableChangeRequest(module, blockKey, id, level, lang, token, qp))

    const response = yield race({
        success: take(PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST_SUCCESS(id)),
        failed: take(PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE_REQUEST_FAILED(id)),
    })

    if (!response.failed) {
        const data = response?.success?.payload?.response?.body?.beneficialOwners
        const query = response?.success?.payload?.response?.body?.query ?? []
        const pagination = query?.pagination
        const filter = query?.filter
        const sorted = _.reduce(
            query?.sort?.sortedColumns,
            (acc, val) => [
                ...acc,
                {
                    id: val?.columm, // TYPO FROM BACKEND
                    desc: val?.direction === 'desc',
                },
            ],
            []
        )

        const newCustomData = {
            data,
            pagination,
            sorted,
            filter,
            isLoading: false,
        }

        yield put(personsBeneficialOwnersTableDataReceived(module, id, blockKey, newCustomData))
    } else {
        yield put(personsBeneficialOwnersTableLoadingFalse(module, id, blockKey, { isLoading: false }))
    }

    return null
}

function* personsBeneficialOwnersChangeCustomData(action) {
    const blockKey = action?.data?.blockKey
    if (blockKey !== config.modules.report.blocks.persons.key) return null

    const module = action?.data?.module
    const id = action?.data?.id
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, blockKey, id)
    const incomingChange = action?.data?.customData

    const newCustomData = {
        ...customData,
        beneficialOwners: {
            ...(customData?.beneficialOwners ?? {}),
            ...incomingChange,
        },
    }

    yield put(blockSaveCustomData(module, id, blockKey, newCustomData))

    return null
}

export default function* beneficialOwnersSaga() {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(PERSONS_BENEFICIAL_OWNERS_TABLE_LOADING_TRUE, personsBeneficialOwnersChangeCustomData),
        takeEvery(PERSONS_BENEFICIAL_OWNERS_TABLE_LOADING_FALSE, personsBeneficialOwnersChangeCustomData),
        takeEvery(PERSONS_BENEFICIAL_OWNERS_TABLE_DATA_RECEIVED, personsBeneficialOwnersChangeCustomData),
        takeLatest(PERSONS_BENEFICIAL_OWNERS_TABLE_CHANGE, personsBeneficialOwnersTableSaga),
    ])
}
